import { Procedure } from '../define-procedure';
import { Store, Actions, ofActionDispatched, ofActionCompleted } from '@ngxs/store';
import { MessageEnum } from '../models/message.enum';
import { EventsEnum } from 'src/app/engine/models/events.enum';
import { RPCEvent } from '../define-event';
import { AnimationService } from 'src/app/engine/animation.service';
import {
  NextChapter,
  PreviousChapter,
  SelectChapterById,
} from 'src/app/story/state/chapters/story.chapters.actions';
import { StoryChaptersState } from 'src/app/story/state/chapters/story.chapters.state';

import { TimelineIO } from '../models/timeline.io';
import { flatMap, take, delay } from 'rxjs/operators';
import { from, Observable } from 'rxjs';
import { ViewService } from 'src/app/view/view.service';

type TimelineProcRequest = TimelineIO.TimelineSetRequest | TimelineIO.TimelinePlayRequest | void;

type TimelineProcResponse =
  | TimelineIO.TimelinePlayResponse
  | TimelineIO.TimelinePauseResponse
  | TimelineIO.TimelineNextChapterResponse
  | TimelineIO.TimelinePreviousChapterResponse
  | TimelineIO.TimelineInfoResponse
  | TimelineIO.TimelineSetResponse
  | TimelineIO.TimelineQueryResponse;

type TimelineEventResponse = TimelineIO.TimelineTransitionResponse | true;

export function getTimelineEvents(): RPCEvent<TimelineEventResponse>[] {
  return [
    {
      eventName: EventsEnum.TimelinePlaying,
      mapFn: (event) => event,
    },
    {
      eventName: EventsEnum.TimelineCompleted,
      mapFn: (event) => true,
    },
    {
      eventName: EventsEnum.TimelineChapterTransition,
      aliasEvents: [EventsEnum.TimelineChaptersActivated],
      mapFn: (event) => {
        return {
          previousChapter: event.oldChapterIndex,
          currentChapter: event.newChapterIndex,
          time: event.time,
        };
      },
    },
  ];
}

function formatTimelineInfo(
  store: Store,
  animationService: AnimationService,
): TimelineIO.TimelineInfoResponse {
  const timeframe = animationService.getTimeFrame();

  return {
    time: animationService.getTime(),
    startTime: timeframe.firstTime,
    endTime: timeframe.lastTime,
    chapters: store
      .selectSnapshot(StoryChaptersState.getStoryChapters)
      .map((chapter) => chapter.id),
    playing: animationService.isPlaying(),
  };
}

export function getTimelineProcedures(
  store: Store,
  actions: Actions,
  animationService: AnimationService,
  viewService: ViewService,
): Procedure<TimelineProcRequest, TimelineProcResponse>[] {
  return [
    {
      // Support Timeline.query according to J&J's usage.
      // Only need to support the data specified in TimelineIO.TimelineQueryResponse
      procName: MessageEnum.TimelineQuery,
      actionFn: () => {
        const chapters = store
          .selectSnapshot(StoryChaptersState.getStoryChapters)
          .map((chapter) => {
            const { description, displayName } = chapter.info;
            const annotations = chapter.labels.map((label) => {
              return {
                title: label.title,
                description: label.description,
              };
            });

            return {
              description,
              displayName,
              annotations,
            };
          });

        return { chapters };
      },
      hideDefinition: true,
    },
    {
      procName: MessageEnum.TimelineInfo,
      actionFn: () => formatTimelineInfo(store, animationService),
    },
    {
      procName: MessageEnum.TimelinePlay,
      actionFn: (params: TimelineIO.TimelinePlayRequest) => {
        // hoops to jump through:
        // The app is responsible for managing chapter state,
        // so we have to dispatch a chapter change, and wait until its done before we can play
        // we also have to map from global time to chapter, in the case of chapterId being absent from params.

        const loop = params.loop || false;

        if (params.time === undefined && params.chapterId === undefined) {
          const currentChapterId = store.selectSnapshot(StoryChaptersState.getSelectedChapterId);
          animationService.play({
            numChapters: 1,
            loop,
            queue: false,
            startChapterId: currentChapterId,
          });
          return true;
        }

        // time should not be lower than zero
        const timeLowerBound = Math.max(0, params.time);

        // time should not be greater than upper timeline bound
        const timeUpperBound = animationService.getTimeFrame().lastTime;
        const offsetToPreventPlayBleedingToZeroethChapter = 0.001;
        const adjTime = Math.min(
          timeLowerBound,
          timeUpperBound - offsetToPreventPlayBleedingToZeroethChapter,
        );

        return new Observable((observer) => {
          // wait for chapter change
          actions.pipe(ofActionCompleted(SelectChapterById), take(1)).subscribe(() => {
            const playParams = { numChapters: 1, queue: false, loop };

            params.time !== undefined
              ? animationService.play({ ...playParams, startTime: adjTime })
              : animationService.play({ ...playParams, startChapterId: params.chapterId });

            observer.next(true);
            observer.complete();
          });

          const chapterId: string = (() => {
            if (params.chapterId) {
              return params.chapterId;
            }
            return animationService.mapGlobalTimeToLocal(
              adjTime,
              // weird border boundary nonsense.  Check the comments on the definition of mapGlobalTimeToLocalTime in the Engine.
              // if we are playing from time zero, we should start at the zeroth chapter.
              // at time 0, the last chapter is the 'first' chapter because it's to the left of the boundary. O_o
              { tie: 'later' },
            ).branch.id;
          })();

          store.dispatch(new SelectChapterById(chapterId));
        });
      },
    },
    {
      procName: MessageEnum.TimelinePause,
      actionFn: () => {
        animationService.pause();
        return true;
      },
    },
    {
      procName: MessageEnum.TimelineSet,
      actionFn: (params: TimelineIO.TimelineSetRequest) => {
        // TimelineSet requires jumping through some hoops.
        // The application is now responsible for managing chapter changes
        // The engine must only be used for scrubbing time, not chapters.
        // So, the strategy is:  Set chapter - then scrub to a time within chapter, if needed.

        // noop
        if (params.time === undefined && params.chapterId === undefined) {
          return true;
        }

        if (params.chapterId !== undefined) {
          store.dispatch(new SelectChapterById(params.chapterId));
          return true;
        }

        // time should not be lower than zero
        const timeLowerBound = Math.max(0, params.time);

        // time should not be greater than upper timeline bound
        const timeUpperBound = animationService.getTimeFrame().lastTime;
        const adjTime = Math.min(timeLowerBound, timeUpperBound);

        return new Observable((observer) => {
          // listen for chapter changes, so we know it's safe to scrub
          actions.pipe(ofActionCompleted(SelectChapterById), take(1)).subscribe(() => {
            // confusing "first chapter is really the 'next' chapter paradigm"
            // read comments at definition for Root.prototype.mapGlobalTime
            animationService.scrub({ time: adjTime, which: adjTime === 0 ? 'second' : 'first' });

            // safe to proceed
            observer.next(true);
            observer.complete();
          });

          const mapped = animationService.mapGlobalTimeToLocal(
            adjTime,
            adjTime === 0 ? { tie: 'later' } : {},
          );
          store.dispatch(new SelectChapterById(mapped.branch.id));
        });
      },
    },
    {
      procName: MessageEnum.TimelineNext,
      actionFn: () => {
        viewService.nextChapter('API');
        return true;
      },
    },
    {
      procName: MessageEnum.TimelinePrev,
      actionFn: () => {
        viewService.previousChapter('API');
        return true;
      },
    },
  ];
}
